/**
 * This file contains the state machine, that handles the state of the product-detail modal:
 *  - handles product OR cart item data fetching
 *  - manages currently viewed product / cart item and its selected addons and sum of selected-addon prices
 *  - caches already viewed products from the list
 */
import { assign, createMachine, MachineConfig, MachineOptions } from 'xstate'

import { captureWithSentry } from '~logic/sentry/captureWithSentry'

import { TProductDetailMachineTypes } from './productDetailMachineTypes'

type TContext = TProductDetailMachineTypes['context']
type TEvent = TProductDetailMachineTypes['events']
type TTypeState = TProductDetailMachineTypes['typeState']
type TStateSchema = TProductDetailMachineTypes['schema']

const machineConfig: MachineConfig<TContext, TStateSchema, TEvent> = {
  predictableActionArguments: true,
  id: 'productDetail',
  initial: 'idle',
  context: {
    amount: null,
    productId: null,
    promoTag: null,
    categoryId: null,
    editedItemIds: null,
    product: null,
    addonCategories: null,
    note: null,
    productCache: new Map(),
    addonCategoriesFulfilled: false,
  },
  on: {
    CLEAR_CACHE: { actions: ['clearCache'] },
    CLEAR_PRODUCT: { target: '#idle', actions: ['resetProductState'] },
    SELECT_PRODUCT: [
      { target: '#loaded', cond: 'isCached', actions: ['updateContext', 'setProduct'] },
      { target: '#loading', actions: ['updateContext'] },
    ],
  },
  states: {
    idle: {
      id: 'idle',
    },
    loading: {
      id: 'loading',
      invoke: {
        id: 'fetchProductDetail',
        src: 'fetchProductDetail',
        onDone: {
          target: '#loaded',
          actions: ['setProduct'],
        },
        onError: {
          target: '#errored',
        },
      },
    },
    loaded: {
      id: 'loaded',
      initial: 'editing',
      states: {
        editing: {
          id: 'editing',
          entry: ['evaluateAddonCategoriesStatus'],
          on: {
            UPDATE_AMOUNT: { actions: 'updateContext' },
            UPDATE_NOTE: { actions: 'updateContext' },
            UPDATE_ADDON_STATE: { actions: ['setAddonState', 'evaluateAddonCategoriesStatus'] },
            ADD_ITEM_TO_CART: { target: '#submitted' },
          },
        },

        submitted: {
          id: 'submitted',
          always: { target: '#idle', actions: ['resetProductState'] },
        },

        errored: {
          id: 'errored',
        },
      },
    },
  },
}

const machineOptions: Partial<MachineOptions<TContext, TEvent>> = {
  //* ACTIONS --------------------------------------------------------------------//
  actions: {
    resetProductState: assign((context) => {
      return {
        ...context,
        note: null,
        amount: null,
        product: null,
        productId: null,
        promoTag: null,
        categoryId: null,
        editedItemIds: null,
        addonCategories: null,
        addonCategoriesFulfilled: false,
      }
    }),

    setProduct: assign((context, event) => {
      // set & store fetched product/cart item
      if (event.type === 'done.invoke.fetchProductDetail') {
        const product = event.data

        // if it is NOT a cartItem, add product data to the productCache
        if (context.editedItemIds === null) {
          context.productCache.set(product.id, product)
        }

        return {
          ...context,
          product,
          note: product.note,
          amount: product.amount,
          addonCategories: product.addonCategories,
        }
      }

      // restore the product from the productCache
      if (event.type === 'SELECT_PRODUCT') {
        const { productId } = event.payload
        const product = context.productCache.get(productId ?? ``)

        if (!product) {
          // this should never happen, because this code should only run after `isCached` guard is evaluated to true, therefore productCache should contain the record of productId
          captureWithSentry({ error: `product ID: ${productId} is not in the cache` })

          if (productId) {
            context.productCache.delete(productId)
          }

          return context
        }

        return {
          ...context,
          product,
          note: product.note,
          amount: product.amount,
          addonCategories: product.addonCategories,
        }
      }

      return context
    }),

    setAddonState: assign((context, event) => {
      if (event.type !== 'UPDATE_ADDON_STATE' || context.addonCategories === null) {
        return context
      }

      const updatedCategory = event.payload.category

      const updatedCategories = context.addonCategories.map((category) => {
        // update just the amount of each addon in the category
        // keep the rest of the addon data the same,
        // so we don't lose the original addon data such as sideDishCombinationMealPrice
        const addons = category.addons.map((addon) => {
          const newAddon = updatedCategory.addons.find(
            (updatedAddon) => updatedAddon.id === addon.id,
          )
          const newAddonAmount = newAddon?.amount ?? addon.amount

          return {
            ...addon,
            amount: newAddonAmount,
          }
        })

        return category.id === updatedCategory.id ? { ...category, addons } : category
      })

      return {
        ...context,
        addonCategories: updatedCategories,
      }
    }),

    evaluateAddonCategoriesStatus: assign((context) => {
      if (context.addonCategories === null) {
        return context
      }

      const addonCategoriesFulfilled = context.addonCategories.every((category) => {
        const { minTotalCount } = category
        const selectedAddonCount = category.addons.reduce((sum, addon) => sum + addon.amount, 0)

        return minTotalCount === undefined || selectedAddonCount >= minTotalCount
      })

      return {
        ...context,
        addonCategoriesFulfilled,
      }
    }),

    updateContext: assign((context, event) => {
      if (
        event.type !== 'SELECT_PRODUCT' &&
        event.type !== 'UPDATE_AMOUNT' &&
        event.type !== 'UPDATE_NOTE'
      ) {
        return context
      }

      return {
        ...context,
        ...event.payload,
      }
    }),

    clearCache: assign((context, event) => {
      if (event.type === 'CLEAR_CACHE') {
        context.productCache.clear()
      }

      return context
    }),
  },

  //* GUARDS --------------------------------------------------------------------//
  guards: {
    // if the product from the product list has been viewed before, the product data should be loaded from the productCache and not fetched again
    isCached: (context, event) => {
      if (event.type !== 'SELECT_PRODUCT' || event.payload.editedItemIds !== null) {
        return false
      }

      return context.productCache.has(event.payload.productId)
    },
  },
}

export const productDetailMachine = createMachine<TContext, TEvent, TTypeState>(
  machineConfig,
  machineOptions,
)
